import { Button, DatePicker, Space } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { DFilters } from "../Dashboard";

const { RangePicker } = DatePicker;

// Calculate difference in days using Dayjs
const getDaysDifference = (range: [Dayjs, Dayjs] | null) => {
  if (!range || !range[0] || !range[1]) return 0;
  return Math.abs(range[1].diff(range[0], "day")) + 1; // +1 to include both start and end
};

const formatRange = (range: [Dayjs, Dayjs] | null) => {
  if (!range || !range[0] || !range[1]) return "";

  return `${range[0].format("DD MMM, YYYY")} - ${range[1].format(
    "DD MMM, YYYY",
  )}`;
};

type Props = {
  handleFilter: <K extends keyof DFilters>(
    filterType: K,
    value: DFilters[K],
  ) => void;
};

const DateRangePicker = ({ handleFilter }: Props) => {
  const [tempRange, setTempRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs] | null>(
    null,
  );
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleConfirm = () => {
    setSelectedRange(tempRange || selectedRange);

    setOpen(false);
  };

  const handleCancel = () => {
    setTempRange(null);
    setOpen(false);
  };

  useEffect(() => {
    if (selectedRange?.[0] && selectedRange?.[1]) {
      handleFilter("fromDate", dayjs(selectedRange?.[0]).format("YYYY-MM-DD"));
      handleFilter("toDate", dayjs(selectedRange?.[1]).format("YYYY-MM-DD"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange]);

  // Detect clicks outside the input and the calendar popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const popup = document.querySelector(".ant-picker-dropdown");
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !(popup && popup.contains(event.target as Node))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <Space direction="vertical" ref={inputRef}>
      <RangePicker
        allowClear
        value={tempRange || selectedRange}
        open={open}
        onCalendarChange={(dates) => setTempRange(dates as [Dayjs, Dayjs])}
        onChange={(dates) => {
          if (!dates) {
            // Clear pressed
            setTempRange(null);
            setSelectedRange(null);
            handleFilter("fromDate", undefined);
            handleFilter("toDate", undefined);
          }
        }}
        onOpenChange={(status) => setOpen(true)}
        size="large"
        className="h-9 w-[275px]"
        format={"DD MMM, YYYY"}
        renderExtraFooter={() => (
          <div>
            <Space className="px-4 pt-1">
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="4" cy="4" r="4" fill="#5E718D" />
              </svg>
              {formatRange(tempRange || selectedRange)}{" "}
              <svg
                width="14"
                height="2"
                viewBox="0 0 14 2"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1H13"
                  stroke="#AFBACA"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              {getDaysDifference(tempRange || selectedRange)} Days
            </Space>
            <Space className="flex justify-between px-4 pb-4">
              <Button
                size="middle"
                className="button-bordered-light h-10 w-[130px]"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                size="middle"
                type="primary"
                className="h-10 w-[130px]"
                onClick={handleConfirm}
              >
                Select Date
              </Button>
            </Space>
          </div>
        )}
      />
    </Space>
  );
};

export default DateRangePicker;
